import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { Link } from 'react-router-dom'
import { connect } from 'react-redux'
import { compose, withState, withHandlers } from 'recompose'
import classNames from 'classnames'

import { selectors as ConfigSelectors } from 'data/config'
import { signInFromEmailAndPassword } from 'data/auth/actions'
import { selectors as OrganizationsSelectors } from 'data/organizations'
import RoutesHelper from 'helpers/RoutesHelper'
import DesktopHelpers from 'helpers/DesktopHelpers'

import { Text, Button } from 'components/base'
import { InputWithLabel } from 'components/Utils/Form'
import * as Icons from 'components/Utils/Icons'
import { getReturnToFromLocation } from './utils'

const PROVIDER_SLACK = Symbol('slack')
const PROVIDER_GOOGLE = Symbol('google')
const PROVIDER_OPEN_ID = Symbol('openId')

class SigninButton extends Component {
  static propTypes = {
    onClick: PropTypes.func.isRequired,
    text: PropTypes.string.isRequired,
    className: PropTypes.string,
    center: PropTypes.bool,
    icon: PropTypes.node.isRequired,
    authLoginItem: PropTypes.func,
  }

  render() {
    const { onClick, text = '', icon, className } = this.props
    return (
      <Button.Login
        type="button"
        className={classNames(className)}
        onClick={onClick}
        onMouseOver={this.onMouseOver}
        onMouseLeave={this.onMouseOut}
      >
        {icon}
        {text}
      </Button.Login>
    )
  }
}

class SlackButton extends Component {
  render() {
    const icon = <Icons.SlackIcon size={16} className="margin-right-normal" />
    return <SigninButton {...this.props} icon={icon} />
  }
}

class GoogleButton extends Component {
  render() {
    const icon = <Icons.GoogleIcon size={16} className="margin-right-normal" />

    return <SigninButton {...this.props} icon={icon} />
  }
}

class AuthLogin extends Component {
  static propTypes = {
    signInFromEmailAndPassword: PropTypes.func.isRequired,
    email: PropTypes.string.isRequired,
    password: PropTypes.string.isRequired,
    setEmail: PropTypes.func.isRequired,
    setPassword: PropTypes.func.isRequired,
  }

  state = { forceEmailPassword: false }

  renderTitle() {
    const { organization } = this.props
    return (
      <div className="flexbox flexbox-column margin-bottom-large">
        <div className="center-text margin-bottom-normal shade-05 font-size-larger font-weight-medium margin-bottom-medium">
          Sign into
        </div>
        <div className="flexbox flexbox-centered">
          <div>
            {organization.photoURL && (
              <img
                alt="organization logo"
                style={{ height: 32, width: 32, verticalAlign: 'middle' }}
                className="margin-right-normal radius-round border-small border-shade-50"
                src={organization.photoURL}
              />
            )}
          </div>
          <Text.Title>{organization.name}</Text.Title>
        </div>
      </div>
    )
  }

  renderGoToSSO = () => {
    return (
      <div
        key="go-to-sso"
        className="margin-bottom-normal shade-20 center-text"
      >
        Or,{' '}
        <Text.ExternalLink
          inline
          onClick={() => {
            this.setState({ forceEmailPassword: false })
          }}
        >
          use your team's Single sign-on authentication
        </Text.ExternalLink>
      </div>
    )
  }

  renderGoToAdminPassword = () => {
    return (
      <div key="go-to-login" className="shade-20 center-text">
        Team admins can{' '}
        <Text.ExternalLink
          inline
          onClick={() => {
            this.setState({ forceEmailPassword: true })
          }}
        >
          sign in here
        </Text.ExternalLink>
      </div>
    )
  }

  handleLoginEmailPassword = event => {
    event.preventDefault()
    const {
      email,
      password,
      signInFromEmailAndPassword,
      location,
      appDomain,
    } = this.props
    const returnTo = getReturnToFromLocation(location, appDomain)

    signInFromEmailAndPassword({
      email,
      password,
      returnTo,
    })
  }

  renderEmailPassword() {
    const { email, password, setEmail, setPassword, organization } = this.props
    return (
      <form
        className="width-full flexbox flexbox-column"
        style={{
          padding: '0 2px',
        }}
        onSubmit={this.handleLoginEmailPassword}
      >
        <GoogleButton
          className="margin-vertical-small"
          text="Sign in with Google"
          onClick={() => this.handleSignInWithProvider(PROVIDER_GOOGLE)}
        />
        <SlackButton
          className="margin-vertical-small"
          text="Sign in with Slack"
          onClick={() => this.handleSignInWithProvider(PROVIDER_SLACK)}
        />
        <div className="flexbox flexbox-justify-center shade-30 font-weight-medium padding-normal">
          or
        </div>
        <InputWithLabel
          label="Email"
          id="email"
          type="email"
          tabIndex="1"
          onChange={setEmail}
          placeholder="john@acme.inc"
          value={email}
        />
        <InputWithLabel
          label={
            <div className="flexbox flexbox-grow flexbox-space-between flexbox-align-center">
              <div className="flexbox-grow">Password</div>
              <Link
                className="flexbox-no-grow font-weight-medium shade-20 font-size-small"
                to={RoutesHelper.requestResetPassword()}
                tabIndex="4"
              >
                Forgot your password?
              </Link>
            </div>
          }
          placeholder="password"
          id="password"
          type="password"
          onChange={setPassword}
          tabIndex="2"
          value={password}
        />
        <Button.Primary type="submit" tabIndex="3">
          Sign in to {organization.name}
        </Button.Primary>
      </form>
    )
  }

  handleSignInWithProvider = provider => {
    const {
      apiEndpoint,
      organization: { id },
      location,
      appDomain,
    } = this.props
    const returnTo = getReturnToFromLocation(location, appDomain)

    switch (provider) {
      case PROVIDER_GOOGLE:
        const isAppDesktop = DesktopHelpers.isElectronGoogleAuthSupported()
        const url = `${apiEndpoint}/auth/google/login?loginOrigin=${
          isAppDesktop ? 'desktop' : 'app'
        }&organizationId=${id}&returnTo=${returnTo}`
        if (isAppDesktop) {
          window.open(url)
        } else {
          window.location = url
        }
        return
      case PROVIDER_SLACK:
        window.location = `${apiEndpoint}/auth/slack/login?organizationId=${id}&returnTo=${returnTo}`
        return
      case PROVIDER_OPEN_ID:
        window.location = `${apiEndpoint}/auth/openid/login?organizationId=${id}&returnTo=${returnTo}`
        return
      default:
        throw new Error('Unkown provider')
    }
  }

  isSso() {
    const { organization } = this.props
    const { forceEmailPassword } = this.state
    return (
      organization.get('authMethod') !== 'emailPassword' && !forceEmailPassword
    )
  }

  renderSSO = () => {
    const { organization } = this.props
    const isGoogleSSO = organization.get('authMethod') === 'google'
    return (
      <React.Fragment>
        <div className="font-size-large shade-10 margin-bottom-medium line-height-larger">
          This team requires you to sign in with your <br />
          <span className="font-weight-medium">
            {isGoogleSSO
              ? 'Google Apps'
              : organization.authProvider.get('name')}
          </span>{' '}
          account.
        </div>
        {isGoogleSSO ? (
          <GoogleButton
            text="Sign in with your Google account"
            onClick={() => this.handleSignInWithProvider(PROVIDER_GOOGLE)}
          />
        ) : (
          <SigninButton
            onClick={() => this.handleSignInWithProvider(PROVIDER_OPEN_ID)}
            text={`Sign in with ${organization.authProvider.get('name')}`}
          />
        )}
      </React.Fragment>
    )
  }

  renderContent() {
    if (this.props.organization.get('authMethod') === 'emailPassword') {
      return null
    }
    return this.state.forceEmailPassword
      ? this.renderGoToSSO()
      : this.renderGoToAdminPassword()
  }

  render() {
    return (
      <div className="height-full flexbox-grow flexbox flexbox-column">
        {this.renderTitle()}
        <div className="flexbox flexbox-column">
          {this.isSso() ? this.renderSSO() : this.renderEmailPassword()}
        </div>
        <div className="margin-top-larger">{this.renderContent()}</div>
      </div>
    )
  }
}

export default compose(
  withState('email', 'setEmail', ''),
  withState('password', 'setPassword', ''),
  withHandlers({
    setEmail: ({ setEmail }) => event => setEmail(event.target.value),
    setPassword: ({ setPassword }) => event => setPassword(event.target.value),
  }),
  connect(
    state => ({
      organization: OrganizationsSelectors.getOrganization(state),
      boardingAppRoot: ConfigSelectors.getBoardingAppRoot(state),
      apiEndpoint: ConfigSelectors.getApiEndpoint(state),
      appDomain: ConfigSelectors.getAppDomain(state),
    }),
    { signInFromEmailAndPassword }
  )
)(AuthLogin)
